const LOCALE = 'de-DE';

/** Cents → "1.234,50 €". */
export function formatMoney(cents: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

/** Cents → "1.235 €" – for tiles and axes where decimals are noise. */
export function formatMoneyShort(cents: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/** Cents → "12,3 Tsd. €" for axis ticks on large ranges. */
export function formatMoneyCompact(cents: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(cents / 100);
}

export function formatNumber(value: number, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits }).format(value);
}

/** "2026-04-06" → "6. Apr. 2026". Invalid or missing input yields "–". */
export function formatDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '–';
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '–';
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/**
 * A planned window of whole months: "April 2026", "April – Juni 2026" or
 * "Dez. 2026 – Jan. 2027". Within one year the year is named only once.
 */
export function formatMonthRange(from: string | null, to: string | null): string {
  if (!from) return 'Noch nicht terminiert';
  if (!to || to === from) return formatMonth(from);
  if (from.slice(0, 4) === to.slice(0, 4)) {
    return `${formatMonthName(from)} – ${formatMonth(to)}`;
  }
  return `${formatMonthShort(from)} – ${formatMonthShort(to)}`;
}

/** "2026-04" → "April" – the month on its own, for a range inside one year. */
function formatMonthName(month: string): string {
  const date = new Date(`${month}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return month;
  return new Intl.DateTimeFormat(LOCALE, { month: 'long' }).format(date);
}

/** "2026-04" → "April 2026". */
export function formatMonth(month: string): string {
  const date = new Date(`${month}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return month;
  return new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric' }).format(date);
}

/** "2026-04" → "Apr 26" for tight axis labels. */
export function formatMonthShort(month: string): string {
  const date = new Date(`${month}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return month;
  return new Intl.DateTimeFormat(LOCALE, { month: 'short', year: '2-digit' }).format(date);
}

/**
 * Parses a German or plain decimal input ("1.234,50", "1234.5") into cents.
 *
 * A dot followed by exactly three digits is a German thousands separator, so
 * "4.205" is 4 205 € — never 4,205 €. That is unambiguous here because
 * `centsToInput` never emits more than two decimal places, so a three-digit
 * group can only have been typed as grouping. A comma is always the decimal
 * separator.
 *
 * Returns null for anything that is not a number, so callers can keep the
 * field empty rather than writing a 0.
 */
export function parseMoneyToCents(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const normalised = trimmed
    .replace(/[€\s ]/g, '')
    .replace(/\.(?=\d{3}\b)/g, '')
    .replace(',', '.');
  const value = Number(normalised);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100);
}

/** Cents → the plain string an <input> should show ("1234.5"). */
export function centsToInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return String(cents / 100);
}

export function parseQuantity(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed.replace(',', '.'));
  return Number.isFinite(value) && value >= 0 ? value : null;
}
